import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import blogsData from "../Components/Data/Blog"; // Adjust path if needed

const BlogPage = styled.div`
  display: flex;
  gap: 2rem;
  max-width: 1200px;
  margin: 3rem auto;
  padding: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1rem;
  }
`;

const BlogContent = styled.div`
  flex: 3;
  background: #ffffff;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);

  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #222222;
    margin-bottom: 1rem;
  }

  .gallery {
    margin-top: 2rem;

    h3 {
      font-size: 1.5rem;
      color: #222222;
      margin-bottom: 1rem;
    }

    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 1rem;

      img {
        width: 100%;
        height: auto;
        border-radius: 8px;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        transition: transform 0.2s ease;

        &:hover {
          transform: scale(1.05);
        }
      }
    }

    .empty-gallery {
      color: #666666;
      font-size: 1.2rem;
    }
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;

    .meta-item {
      display: flex;
      align-items: center;
      font-size: 0.95rem;
      color: #555555;
      margin-right: 1.5rem;
    }

    .meta-item svg {
      width: 20px;
      height: 20px;
      fill: #0077cc;
      margin-right: 0.5rem;
      transition: transform 0.2s ease, fill 0.2s ease;
    }

    .meta-item svg:hover {
      transform: scale(1.2);
      fill: #ff5757;
    }
  }
`;

const Lightbox = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  img {
    max-width: 90%;
    max-height: 80%;
    border-radius: 8px;
  }

  .close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    background: #ffffff;
    color: #222222;
    font-size: 1.5rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
  }
`;

const Sidebar = styled.div`
  flex: 1;
  background: #f9f9f9;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #222222;
    margin-bottom: 1.5rem;
    border-left: 4px solid #0077cc;
    padding-left: 0.5rem;
  }

  .recent-post {
    display: flex;
    margin-bottom: 1rem;

    img {
      width: 60px;
      height: 60px;
      border-radius: 8px;
      margin-right: 0.5rem;
      object-fit: cover;
    }

    .post-details {
      display: flex;
      flex-direction: column;

      .title {
        font-size: 1rem;
        color: #0077cc;
        text-decoration: none;
        font-weight: 600;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
`;
const CommentSection = styled.div`
  margin-top: 2rem;
  background: #f9f9f9;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #222222;
    margin-bottom: 1rem;
  }

  .comments {
    margin-bottom: 1rem;

    .comment {
      margin-bottom: 1rem;
      padding: 1rem;
      background: #ffffff;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

      .author {
        font-weight: bold;
        color: #0077cc;
      }

      .message {
        color: #555555;
      }
    }
  }

  form {
    display: flex;
    flex-direction: column;

    textarea {
      resize: none;
      height: 100px;
      margin-bottom: 1rem;
      padding: 1rem;
      border: 1px solid #dddddd;
      border-radius: 8px;
      font-size: 1rem;
      font-family: inherit;

      &:focus {
        border-color: #0077cc;
        outline: none;
      }
    }

    button {
      background: #0077cc;
      color: #ffffff;
      padding: 0.75rem 1.5rem;
      border: none;
      border-radius: 8px;
      font-size: 1rem;
      cursor: pointer;
      

      &:hover {
        background: #005fa3;
      }
    }
  }
`;

const BlogDetail = () => {
  const { link } = useParams(); // Get the dynamic part of the URL
  const [lightboxImage, setLightboxImage] = useState(null);

  const openLightbox = (img) => setLightboxImage(img);
  const closeLightbox = () => setLightboxImage(null);

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (newComment.trim()) {
      setComments([
        ...comments,
        { id: Date.now(), author: "Anonymous", message: newComment },
      ]);
      setNewComment("");
    }
  };


  // Match the blog based on the link parameter
  const blog = blogsData.mostRecent.find((b) => b.link.endsWith(link));

  if (!blog) {
    return <h1>Blog Not Found</h1>;
  }

  return (
    <BlogPage>
      <BlogContent>
        <img
          src={blog.image}
          alt={blog.title}
          style={{
            width: "100%",
            borderRadius: "2%",
          }}
        />
        <h1>{blog.title}</h1>
        <div className="meta">
          <div className="meta-item">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8z"></path>
              <path d="M11 6h2v6h-2zm0 7h2v2h-2z"></path>
            </svg>
            {blog.date}
          </div>
          <div className="meta-item">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"></path>
            </svg>
            {blog.comments} Comments
          </div>
          <div className="meta-item">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm-1-9H7v-2h4V7l5 5-5 5v-4z"></path>
            </svg>
            {blog.views} Views
          </div>
        </div>
        <div
          className="content"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />
        <div className="gallery">
          <h3>Gallery</h3>
          {blog.galleryImages && blog.galleryImages.length > 0 ? (
            <div className="grid">
              {blog.galleryImages.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Gallery Image ${index + 1}`}
                  onClick={() => openLightbox(img)}
                />
              ))}
            </div>
          ) : (
            <p className="empty-gallery">No images available in the gallery.</p>
          )}
        </div>

        {lightboxImage && (
          <Lightbox>
            <img src={lightboxImage} alt="Lightbox" />
            <button className="close-btn" onClick={closeLightbox}>
              Close
            </button>
          </Lightbox>
        )}<CommentSection count={comments.length}>
        <h3>Comments</h3>
        <div className="comments">
          {comments.length > 0 ? (
            comments.map((comment) => (
              <div key={comment.id} className="comment">
                <div className="author">{comment.author}</div>
                <div className="message">{comment.message}</div>
              </div>
            ))
          ) : (
            <div className="empty">No comments yet. Be the first to comment!</div>
          )}
        </div>
        <form onSubmit={handleCommentSubmit}>
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write your comment here..."
          />
          <button type="submit" disabled={!newComment.trim()}>
            Submit Comment
          </button>
        </form>
      </CommentSection>
    </BlogContent>
      <Sidebar>
  <h3>Recent Post</h3>
  {blogsData.mostRecent.slice(0, 3).map((post) => (
    <div className="recent-post" key={post.link}>
      <img src={post.image} alt={post.title} />
      <div className="post-details">
        <a href={`/blogs/${post.link}`} className="title">
          {post.title}
        </a>
        <div className="meta">
          <div style={{ display: 'flex', alignItems: 'center', color: '#025B3A' }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="currentColor"
              style={{ marginRight: '8px', verticalAlign: 'middle' }}
            >
              <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8z"></path>
              <path d="M11 6h2v6h-2zm0 7h2v2h-2z"></path>
            </svg>
            {post.date}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', color: '#025B50' }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="currentColor"
              style={{ marginRight: '8px', verticalAlign: 'middle' }}
            >
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"></path>
            </svg>
            {post.comments}
          </div>
        </div>
      </div>
    </div>
  ))}
</Sidebar>

</BlogPage>
  );
};

export default BlogDetail;
